/**
 * EncryptedVideoPlayer
 * 
 * React component for playing encrypted HLS video streams.
 * Uses hls.js with custom encrypted loader for on-the-fly decryption.
 * 
 * Features:
 * - Automatic HLS initialization with encrypted loader
 * - Playback controls (play/pause/seek)
 * - Loading states and error handling
 * - Quality switching support
 * - Cleanup on unmount
 */

import React, { useRef, useEffect, useState, useMemo } from 'react';
import Hls from 'hls.js';
import { createEncryptedLoader } from './encryptedHlsLoader';
import type { VideoDecryptionKeys } from '@/domain/entities/video/videoDecryptionEntity';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useVideoPlayer } from '@/presentation/contexts/VideoPlayerContext';
import { videoLogger } from '@/domain/services/analytics/videoLogger';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { MediaUtils } from '@/domain/utils/mediaUtils';

export interface EncryptedVideoPlayerProps {
  /** Screen key that will be used for video management and identification */
  screenKey: string;

  /** Video ID */
  videoId: string;

  /** Video URL with CDN token */
  videoUrl: string;

  /** Decryption keys for the video */
  decryptionKeys: VideoDecryptionKeys;

  /** Whether to autoplay */
  autoplay?: boolean;

  /** Whether to loop */
  loop?: boolean;

  /** Whether to mute */
  muted?: boolean;

  /** Custom CSS class */
  className?: string;

  /** Callback when video is ready */
  onReady?: () => void;

  /** Callback when video starts playing */
  onPlay?: () => void;

  /** Callback when video pauses */
  onPause?: () => void;

  /** Callback when video ends */
  onEnded?: () => void;

  /** Callback on error */
  onError?: (error: Error) => void;

  /** Callback when playback actually starts (after initialization) */
  onPlaybackStart?: () => void;

  /** Callback to expose video element for external control */
  onVideoElementReady?: (videoElement: HTMLVideoElement) => void;

  /** Callback when volume changes */
  onVolumeChange?: (volume: number) => void;
}

export const EncryptedVideoPlayer: React.FC<EncryptedVideoPlayerProps> = ({
  screenKey,
  videoId,
  videoUrl,
  decryptionKeys,
  autoplay = false,
  loop = false,
  muted = false,
  className = '',
  onReady,
  onPlay,
  onPause,
  onEnded,
  onError,
  onPlaybackStart,
  onVideoElementReady,
  onVolumeChange
}) => {
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const { getHlsInstance, setHlsInstance, destroyHlsInstance } = useVideoPlayer();
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);

  // Compute video key from screenKey and videoId
  const videoKey = useMemo(() => MediaUtils.composeVideoPlayerKey(screenKey, videoId), [screenKey, videoId]);

  // Store decryption keys in ref to prevent re-initialization when parent re-renders
  // Keys should never change for the same video, so we only need the initial value
  const decryptionKeysRef = useRef<VideoDecryptionKeys>(decryptionKeys);

  // Store callbacks in refs to prevent re-initialization when they change
  const onReadyRef = useRef(onReady);
  const onErrorRef = useRef(onError);
  const onPlayRef = useRef(onPlay);
  const onPauseRef = useRef(onPause);
  const onEndedRef = useRef(onEnded);
  const onPlaybackStartRef = useRef(onPlaybackStart);

  const [error, setError] = useState<string | null>(null);

  // Update callback refs when they change
  useEffect(() => {
    onReadyRef.current = onReady;
    onErrorRef.current = onError;
    onPlayRef.current = onPlay;
    onPauseRef.current = onPause;
    onEndedRef.current = onEnded;
    onPlaybackStartRef.current = onPlaybackStart;
  }, [onReady, onError, onPlay, onPause, onEnded, onPlaybackStart]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      videoLogger.warning('Video element ref is null', videoId);
      return;
    }

    // For encrypted videos, we MUST use HLS.js even on iOS Safari
    // because native HLS can't use our custom decryption loader
    // Native HLS cannot decrypt segments on the fly
    if (!Hls.isSupported()) {
      // On iOS Safari, native HLS won't work for encrypted videos
      const errorMsg = 'Encrypted video playback requires HLS.js which is not supported on this device (iOS Safari < 16)';
      console.error('❌ [EncryptedVideoPlayer]', errorMsg);
      videoLogger.initError(videoId, videoId, new Error(errorMsg), {
        reason: 'hls_js_not_supported',
        device: navigator.userAgent,
        nativeHlsSupport: video.canPlayType('application/vnd.apple.mpegurl'),
        note: 'iOS Safari cannot decrypt HLS segments without HLS.js'
      });
      setError(errorMsg);
      onError?.(new Error(errorMsg));
      return;
    }

    console.log('🔐 [EncryptedVideoPlayer] Initializing HLS for encrypted video:', videoKey, 'on screen', screenKey);

    // Check if HLS instance already exists for this video (shared instance pattern)
    const existingHls = getHlsInstance(videoKey);

    if (existingHls) {
      // Reuse existing HLS instance - attach to this video element
      hlsRef.current = existingHls;

      // Notify ready immediately since HLS is already initialized
      onReadyRef.current?.();

      // Attach the existing HLS instance to this video element
      existingHls.attachMedia(video);

      // If autoplay requested, start playback
      if (autoplay) {
        video.play().catch(err => {
          console.error('❌ Autoplay failed:', err);
          videoLogger.playbackError(videoId, err);
        });
      }
    } else {
      // Create new HLS instance (first time for this video)
      const hls = new Hls({
        loader: createEncryptedLoader(videoId, decryptionKeysRef.current),
        enableWorker: true,
        // Optimize for live streaming with minimal delay.
        // Reduces buffering, faster seeking, but higher CPU usage and potential stuttering
        lowLatencyMode: true,

        // Buffer settings - balance between UX and memory
        backBufferLength: 90,      // Keep 90s for seeking backward
        maxBufferLength: 30,       // Target 30s ahead
        maxMaxBufferLength: 60,    // Never exceed 60s

        // Progressive settings for smoother playback with decryption
        progressive: false,         // Don't play during download (decryption happens after)

        // Retry/timeout settings for network issues
        manifestLoadingTimeOut: 10000,     // 10s to load playlist
        manifestLoadingMaxRetry: 3,        // Retry playlist 3 times
        levelLoadingTimeOut: 10000,        // 10s per quality level
        levelLoadingMaxRetry: 3,           // Retry level 3 times
        fragLoadingTimeOut: 20000,         // 20s per segment (decryption takes time)
        fragLoadingMaxRetry: 6,            // More retries for segments

        // Start quality settings
        startLevel: -1,            // -1 = auto quality based on bandwidth
        capLevelToPlayerSize: true, // Don't load 4K if player is small
      });

      hlsRef.current = hls;

      // Store in context for sharing across components
      setHlsInstance(videoKey, hls);

      // Handle HLS events - MUST be set up BEFORE attachMedia()!
      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        hls.loadSource(videoUrl);
      });

      // Handle manifest parsed event. Call onReady callback here.
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        onReadyRef.current?.();
        // Note: Autoplay is now handled externally via videoElementRegistry
        // The slice will call registry.play() after initialization if needed
      });

      hls.on(Hls.Events.ERROR, (_event, data) => {
        // Log all HLS errors except bufferStalledErrors which are too noisy
        if (data.details !== Hls.ErrorDetails.BUFFER_STALLED_ERROR) {
          videoLogger.hlsError(
            videoId,
            data.type,
            data.details,
            data.fatal || false
          );
        }

        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              hls.startLoad();
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              hls.recoverMediaError();
              break;
            default:
              const errorMsg = `Fatal HLS error: ${data.type}`;
              setError(errorMsg);
              // Notify parent of error
              onErrorRef.current?.(new Error(errorMsg));
              // Destroy and remove from context on fatal error
              destroyHlsInstance(videoKey);
              hls.destroy();
              break;
          }
        }
      });

      // Attach media AFTER setting up event listeners
      setTimeout(() => {
        try {
          hls.attachMedia(video);
        } catch (error) {
          videoLogger.initError(videoId, videoId, error as Error, { stage: 'attach_media' });
        }
      }, 0);
    }

    // Cleanup: detach media but keep HLS instance for reuse
    return () => {
      if (hlsRef.current) {
        hlsRef.current.detachMedia();
        // Note: Do NOT destroy here - we want to reuse the instance
        // Destroy only happens on fatal error or explicit destroyHlsInstance() call
        hlsRef.current = null;
      }
    };
  }, [videoKey, videoUrl, autoplay, getHlsInstance, setHlsInstance, destroyHlsInstance]); // Only depend on stable values

  // Video event handlers
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Expose video element to parent for external control
    onVideoElementReady?.(video);

    // 'play' event fires when playback starts (or is resumed)
    const handlePlay = () => {
      onPlayRef.current?.();
    };

    // 'playing' event fires when playback has actually started (after buffering)
    // This is different from 'play' which fires when play() is called
    const handlePlaying = () => {
      onPlaybackStartRef.current?.();
    };

    const handlePause = () => onPauseRef.current?.();
    const handleEnded = () => onEndedRef.current?.();
    const handleVolumeChange = () => {
      onVolumeChange?.(video.volume);
    };

    const handleError = () => {
      const errorMsg = 'Video playback error';
      videoLogger.playbackError(videoId, new Error(errorMsg), {
        readyState: video.readyState,
        networkState: video.networkState,
        error: video.error ? {
          code: video.error.code,
          message: video.error.message
        } : undefined
      });
      setError(errorMsg);
      onErrorRef.current?.(new Error(errorMsg));
    };

    video.addEventListener('play', handlePlay);
    video.addEventListener('playing', handlePlaying);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('volumechange', handleVolumeChange);
    video.addEventListener('error', handleError);

    return () => {
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('playing', handlePlaying);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('volumechange', handleVolumeChange);
      video.removeEventListener('error', handleError);
    };
  }, [onVideoElementReady, onVolumeChange]);

  // Note: Progress updates are now handled by VideoProgressBar component
  // which queries the video element directly from videoElementRegistry
  // This eliminates the callback chain that was causing parent re-renders

  if (error) {
    return (
      <div
        style={{
          backgroundColor: theme.surface,
          color: theme.text.primary,
          padding: '20px',
          borderRadius: '8px',
          textAlign: 'center'
        }}
      >
        <p>{l10n.content.errorLoadingVideo}</p>
      </div>
    );
  }

  return (
    <div id={`${screenKey}-encrypted-container-${videoId}`}
      className="w-full h-full">
      <video
        id={`${screenKey}-encrypted-${videoId}`}
        ref={videoRef}
        loop={loop}
        muted={muted}
        className={`${className} w-full h-full object-cover`}
        style={{
          backgroundColor: theme.black
        }}
        playsInline
        webkit-playsinline="true"
        x-webkit-airplay="allow"
      />
    </div>
  );
};
